import { ImagePlus } from "lucide-react";
import { useRouter } from "@/lib/nav";
import { useRef, useState } from "react";

import { Button } from "@buildpy/ui/button";
import { Textarea } from "@buildpy/ui/textarea";
import { formatNumber } from "@buildpy/core/lib/format";
import { settleRedirect } from "@/lib/redirect-error";
import { postStaffMessage, postStaffMessagePhoto } from "@/api/actions/message";
import type { MessageActionResult } from "@buildpy/core/modules/message/action-types";
import { postCustomerMessageByLink, postCustomerMessagePhotoByLink } from "@/api/actions/message-share";
import { MAX_MESSAGE_BODY } from "@buildpy/core/modules/message/types";
import { photoSizeBlockReason } from "@buildpy/core/modules/project/photo";
import { resizeImage } from "@/modules/project/ui/resize-image";

type Target = { kind: "staff"; projectId: string } | { kind: "customer"; token: string };

interface Props {
  target: Target;
  placeholder: string;
  testId: string;
}

const FAILED = "보내지 못했습니다. 잠시 뒤 다시 보내 주세요";

// 서버 검사와 같은 코드포인트 기준으로 센다.
const lengthOf = (value: string): number => Array.from(value).length;

const UNREADABLE = "사진을 열 수 없습니다";

const send = (target: Target, body: string, photo: Blob | null): Promise<MessageActionResult> => {
  if (photo) {
    const form = new FormData();
    form.append("file", photo, "photo.jpg");
    form.append("body", body);
    return target.kind === "staff" ? postStaffMessagePhoto(target.projectId, form) : postCustomerMessagePhotoByLink(target.token, form);
  }
  return target.kind === "staff"
    ? postStaffMessage({ project_id: target.projectId, body })
    : postCustomerMessageByLink({ token: target.token, body });
};

// 글 한 칸 + 사진 한 장 + 보내기 — 실시간 없이 보낸 뒤 화면을 다시 읽는다 · 사진은 현장 사진처럼 줄여서 보낸다.
const MessageCompose = ({ target, placeholder, testId }: Props) => {
  const router = useRouter();
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [photo, setPhoto] = useState<Blob | null>(null);
  const [announce, setAnnounce] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const length = lengthOf(body);
  const blank = body.trim() === "" && photo === null;

  const attach = async (list: FileList | null) => {
    const file = list?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setError(null);
    let blob: Blob;
    try {
      blob = await resizeImage(file);
    } catch {
      setError(UNREADABLE);
      return;
    }
    // 줄인 뒤에도 크면 서버 한도에 막히기 전에 여기서 알린다.
    const sizeReason = photoSizeBlockReason(blob.size);
    if (sizeReason) {
      setError(sizeReason);
      return;
    }
    setPhoto(blob);
    setAnnounce("사진 1장을 붙였습니다");
  };

  const detach = () => {
    setPhoto(null);
    setAnnounce("사진을 뺐습니다");
  };

  const submit = async () => {
    setPending(true);
    setError(null);
    try {
      const result = await send(target, body, photo);
      if (!result.ok) {
        setError(result.message);
        if (result.reverify || result.closed) router.refresh();
        return;
      }
      setBody("");
      setPhoto(null);
      setAnnounce("");
      router.refresh();
    } catch (e) {
      if (settleRedirect(e)) return;
      setError(FAILED);
    } finally {
      setPending(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-y-2"
      data-testid={testId}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}>
      <Textarea
        aria-label="대화 내용"
        className="min-h-[88px]"
        data-testid={`${testId}-body`}
        disabled={pending}
        placeholder={placeholder}
        value={body}
        onChange={(event) => setBody(event.target.value)}
      />
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-3">
          <span className={length > MAX_MESSAGE_BODY ? "text-xs text-destructive" : "text-xs text-muted-foreground"}>
            {formatNumber(length)} / {formatNumber(MAX_MESSAGE_BODY)}
          </span>
          {photo ? (
            <span className="flex items-center gap-x-1 text-xs text-muted-foreground" data-testid={`${testId}-photo`}>
              사진 1장
              <Button disabled={pending} size="xs-touch" type="button" variant="link-muted" onClick={detach}>
                빼기
              </Button>
            </span>
          ) : (
            <Button
              data-testid={`${testId}-attach`}
              disabled={pending}
              size="xs-touch"
              type="button"
              variant="link-muted"
              onClick={() => fileRef.current?.click()}>
              <ImagePlus />
              사진 붙이기
            </Button>
          )}
        </div>
        <Button
          data-testid={`${testId}-submit`}
          disabled={pending || blank || length > MAX_MESSAGE_BODY}
          size="sm"
          type="submit">
          {pending ? "보내는 중" : "보내기"}
        </Button>
      </div>
      <input
        ref={fileRef}
        accept="image/*"
        aria-label="대화에 붙일 사진 고르기"
        className="hidden"
        type="file"
        onChange={(event) => void attach(event.target.files)}
      />
      {/* 늘 마운트해 둔다 — 붙이기·빼기를 스크린리더가 읽는다. */}
      <p aria-live="polite" className="sr-only" data-testid={`${testId}-photo-status`} role="status">
        {announce}
      </p>
      {error && (
        <p className="text-sm text-destructive" data-testid={`${testId}-error`} role="alert">
          {error}
        </p>
      )}
    </form>
  );
};

export default MessageCompose;
